import type { FC, KeyboardEvent } from "react";
import {
  isAlpha,
  isDigit,
  isString,
  isValidEmail,
  limit,
  rem,
} from "@huub-web/utils";

import type { DataFieldValue, FormInputType } from "../../types";
import { TextField } from "../../components/TextField";
import { CountryField } from "../../components/CountryField";
import { DateField } from "../../components/DateField";
import { Select } from "../../components/Select";
import type { FormInputFieldProps } from "../../components/FormInput/components/FormInputField";
import type {
  DynamicInputCustomValidationCallback,
  DynamicInputValidationResult,
  DynamicInputValidationState,
} from "../../hooks/useDynamicInputValidation";
import { MonthField } from "../../components/MonthField";
import { NumberField } from "../../components/NumberField";
import { YearField } from "../../components/YearField";
import { InputCheckboxProxy } from "./proxies/InputCheckboxProxy";
import { RadioButtonsProxy } from "./proxies/RadioButtonsProxy";
import { CheckboxGroupProxy } from "./proxies/CheckboxGroupProxy";
import { HiddenFieldProxy } from "./proxies/HiddenFieldProxy";

export type InputConfigurationRecord<T = object> = {
  field: FC<T>;
  props?: Partial<T>;
  onChange?: (config: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    validation: DynamicInputValidationResult;
    onChange?: FormInputFieldProps["onChange"];
    onClose?: FormInputFieldProps["onClose"];
  }) => void;
  /**
   * Optional blur handler called when the input loses focus.
   * Receives the current value and the internal onChange (which runs validation).
   */
  onBlur?: (config: {
    value: DataFieldValue;
    onChange?: (value: DataFieldValue) => void;
  }) => void;
  validation?: DynamicInputValidationState["validationProps"] & {
    validate?: DynamicInputCustomValidationCallback;
    format?: string;
  };
};

const isSystemKey = (e: KeyboardEvent<HTMLInputElement>): boolean => {
  const key = e.key;

  if (
    e.ctrlKey ||
    e.metaKey ||
    e.altKey ||
    e.shiftKey ||
    key === "Backspace" ||
    key === "Tab" ||
    key === "Delete" ||
    key === "Escape" ||
    key === "Enter" ||
    key.startsWith("Arrow") ||
    key === "Home" ||
    key === "End" ||
    key === "Control" ||
    key === "Command"
  ) {
    return true;
  }
  return false;
};

const onKeyDownNumbersOnly = (e: KeyboardEvent<HTMLInputElement>) => {
  if (isSystemKey(e)) {
    return;
  }
  if (!isDigit(e.key)) {
    e.preventDefault();
  }
};

const onKeyDownAlphaOnly = (e: KeyboardEvent<HTMLInputElement>) => {
  if (isSystemKey(e)) {
    return;
  }
  if (!isAlpha(e.key)) {
    e.preventDefault();
  }
};

/**
 * Configuration for all form input types.
 */
export const InputConfiguration: Record<
  FormInputType,
  InputConfigurationRecord
> = {
  string: {
    field: TextField,
    validation: {
      maxLength: 255,
    },
  },
  email: {
    field: TextField,
    props: {
      inputMode: "email",
    },
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.trim?.().toLowerCase(), validation);
    },
    validation: {
      validate: (value) => isValidEmail(value),
      maxLength: 255,
    },
  },
  phone: {
    field: TextField,
    props: {
      inputMode: "number",
    },
    validation: {
      maxLength: 13,
      format: "0612345678, 0201234567, +31612345678",
      validate: (value) =>
        /^(?:\+31\d{9}|0\d{9})$/.test(value?.toString() ?? ""),
      onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
        if (isSystemKey(e) || e.key === "+") {
          return;
        }
        if (!/^[0-9]$/.test(e.key)) {
          e.preventDefault();
        }
      },
    },
  },
  date: {
    field: DateField,
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value, validation);
    },
  },
  country: {
    field: CountryField,
    onChange: ({ value, onChange, onClose, validation }) => {
      onChange?.(value, validation);
      onClose?.();
    },
  },
  select: {
    field: Select,
    props: { popup: { maxWidth: rem(320) } },
    onChange: ({ value, onChange, onClose, validation }) => {
      onChange?.(value, validation);
      onClose?.();
    },
  },
  multiSelect: {
    field: Select,
    props: { multi: true, popup: { maxWidth: rem(320) } },
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value, validation);
    },
  },
  check: {
    field: InputCheckboxProxy,
    props: {},
    onChange: ({ value, onChange, onClose, validation }) => {
      onChange?.(value, validation);
      onClose?.();
    },
  },
  boolean: {
    field: RadioButtonsProxy,
    props: {
      orientation: "horizontal",
    },
  },
  number: {
    field: TextField,
    props: {
      align: "right",
      inputMode: "number",
      type: "number",
      lang: "nl",
    },
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.toString()?.replace(",", "."), validation);
    },
  },
  float: {
    field: TextField,
    props: {
      align: "right",
      inputMode: "decimal",
      type: "number",
      lang: "nl",
    },
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.toString()?.replace(",", "."), validation);
    },
  },
  money: {
    field: NumberField,
    props: {
      lang: "nl",
    },
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.toString()?.replace(",", "."), validation);
    },
  },
  percentage: {
    field: TextField,
    props: {
      align: "right",
      inputMode: "decimal",
      type: "text",
      lang: "nl",
    },
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.toString()?.replace(",", "."), validation);
    },
    /**
     * Normalize and clamp percentage values on blur.
     * - Normalizes shorthand decimals: ".5" → "0.5"
     * - Clamps values to 0–100 range
     * Uses the internal onChange so validation runs on the normalized value.
     */
    onBlur: ({ value, onChange }) => {
      const num = Number(value);
      if (value != null && value !== "" && !Number.isNaN(num)) {
        const clamped = limit(num, 0, 100);
        const normalized = clamped.toString();
        if (normalized !== String(value)) {
          onChange?.(normalized);
        }
      }
    },
    validation: {
      min: 0,
      max: 100,
      format: "0,00",
      /**
       * Validates percentage input: accepts decimals with up to 2 places,
       * optional leading digit (e.g. ",5"), and range 0–100.
       * Normalizes commas to dots before checking because validate() runs
       * on raw input before onChange does the comma→dot conversion.
       */
      validate: (value) => {
        if (!isString(value) || value === "") return true;
        const normalized = value.replace(",", ".");
        if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(normalized)) return false;
        const num = Number(normalized);
        return !Number.isNaN(num) && num >= 0 && num <= 100;
      },
    },
  },
  postalCode: {
    field: TextField,
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.toUpperCase(), validation);
    },
    validation: {
      validate: (value) =>
        isString(value) &&
        /^[0-9]{4}[A-Z]{2}$/.test(value.toUpperCase() as string),
      maxLength: 6,
      format: "1234AA",
      onKeyDown: (e) => {
        if (isSystemKey(e)) {
          return;
        }
        if (!isDigit(e.key) && !isAlpha(e.key)) {
          e.preventDefault();
        }
      },
    },
  },
  /**
   * NLkk BBBB CCCC CCCC CC
   *  • NL = Landcode (Nederland)
   *  • kk = Controlegetal (2 cijfers, berekend volgens de MOD97-10 methode)
   *  • BBBB = Bankcode (4 letters, bijvoorbeeld RABO voor Rabobank)
   *  • CCCC CCCC CC = Rekeningnummer (10 cijfers, indien korter wordt het opgevuld met nullen aan de voorkant)
   * Example: NL12INGB1234123412
   * @variation ^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$ for international IBAN (max 30 char)
   */
  iban: {
    field: TextField,
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.toUpperCase(), validation);
    },
    validation: {
      maxLength: 18,
      format: "NLXX BBBB CCCC CCCC CC",
      validate: (value) =>
        /^NL\d{2}[A-Z]{4}\d{10}$/.test(value?.toString() ?? ""),
      onKeyDown: (e) => {
        if (isSystemKey(e)) {
          return;
        }
        if (!isDigit(e.key) && !isAlpha(e.key)) {
          e.preventDefault();
        }
      },
    },
  },
  /**
   * Een standaard KVK-nummer (Kamer van Koophandel-nummer) in Nederland bestaat uit 8 cijfers.
   * Er zijn geen speciale scheidingstekens, spaties of streepjes in de officiële notatie.
   * Voorbeeld van een correct KVK-nummer: 12345678
   * Veelvoorkomende fouten:
   * ❌ 12.34.56.78 (puntnotatie)
   * ❌ 1234-5678 (streepjes)
   * ❌ NL12345678 (toevoegingen)
   */
  kvkNumber: {
    field: TextField,
    props: {
      inputMode: "number",
    },
    validation: {
      maxLength: 8,
      format: "12345678",
      validate: (value) => /^[0-9]{8}$/.test(value?.toString() ?? ""),
      onKeyDown: onKeyDownNumbersOnly,
    },
  },
  bsn: {
    field: TextField,
    props: {
      inputMode: "number",
    },
    validation: {
      maxLength: 9,
      format: "123456789",
      validate: (value) => /^[0-9]{9}$/.test(value?.toString() ?? ""),
      onKeyDown: onKeyDownNumbersOnly,
    },
  },
  /**
   * idType = EI Europese identiteitskaart
   *    Structuur: 3 letters + 6 cijfers
   *    Voorbeeld: IDN123456
   * idType = PP Paspoort
   *    Structuur: 2 letters + 7 cijfers
   * idType = RB Rijbewijs
   *    Structuur: 1 letter + 9 cijfers
   *    Voorbeeld: B123456789
   * idType = VI Visum
   *    Structuur: 3 letters + 6 cijfers
   *    Voorbeeld: NLD123456
   */
  idNumber: {
    field: TextField,
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.toUpperCase()?.slice(0, 10), validation);
    },
    validation: {
      format: "IDN123456, B123456789, NLD123456",
      maxLength: 10,
      validate: (value) =>
        isString(value) &&
        /^(?:[A-Z]{3}\d{6}|[A-Z]{2}\d{7}|[A-Z]\d{9})$/.test(value),
    },
  },
  permitNumber: {
    field: TextField,
    props: { inputMode: "number" },
    validation: {
      maxLength: 10,
      format: "0123456789",
      validate: (value) => /^[0-9]{10}$/.test(value?.toString() ?? ""),
      onKeyDown: onKeyDownNumbersOnly,
    },
  },
  afmNumber: {
    field: TextField,
    props: { inputMode: "number" },
    validation: {
      maxLength: 7,
      format: "1234567",
      validate: (value) => /^[0-9]{7}$/.test(value?.toString() ?? ""),
      onKeyDown: onKeyDownNumbersOnly,
    },
  },
  nwwiNumber: {
    field: TextField,
    props: { inputMode: "number" },
    validation: {
      maxLength: 10,
      format: "0123456789",
      validate: (value) => /^[0-9]{10}$/.test(value?.toString() ?? ""),
      onKeyDown: onKeyDownNumbersOnly,
    },
  },
  /**
   * Het BAG-nummer (Basisregistratie Adressen en Gebouwen) is een unieke identificatiecode voor een verblijfsobject,
   * pand, ligplaats of standplaats in Nederland. Dit nummer bestaat uit:
   *
   * 16 cijfers, opgebouwd als volgt:
   * - 4 cijfers: Code van de gemeente (op basis van de gemeentecode in de BAG).
   * - 4 cijfers: Nummer van de woonplaats.
   * - 4 cijfers: Nummer van de openbare ruimte.
   * - 4 cijfers: Uniek identificatienummer van het verblijfsobject, pand, ligplaats of standplaats.
   *
   * Een voorbeeld van een BAG-nummer: 1234200012340001
   */
  bagNumber: {
    field: TextField,
    props: { inputMode: "number" },
    validation: {
      maxLength: 16,
      format: "1234200012340001",
      validate: (value) => /^[0-9]{16}$/.test(value?.toString() ?? ""),
      onKeyDown: onKeyDownNumbersOnly,
    },
  },
  /**
   * De HDN Receiver Code is een unieke code binnen het Hypotheken Data Netwerk (HDN), waarmee geldverstrekkers,
   * serviceorganisaties en andere financiële instellingen geïdentificeerd worden.
   *
   * Standaardformaat HDN Receiver Code
   * - Bestaat uit exact 5 hoofdletters (alfabetische tekens, geen cijfers).
   * - Uniek per financiële instelling of partij die is aangesloten bij HDN.
   *
   * Voorbeeld van een HDN Receiver Code
   * - ABNAM (voor ABN AMRO)
   * - RABOB (voor Rabobank)
   */
  hdnReceivercode: {
    field: TextField,
    onChange: ({ value, onChange, validation }) => {
      onChange?.(value?.toUpperCase()?.slice(0, 5), validation);
    },
    validation: {
      format: "ABCDE",
      maxLength: 5,
      validate: (value) => isString(value) && /^[A-Z]{5}$/.test(value),
      onKeyDown: onKeyDownAlphaOnly,
    },
  },
  kifidNumber: {
    field: TextField,
    props: { inputMode: "number" },
    validation: {
      maxLength: 6,
      format: "123456",
      validate: (value) => /^[0-9]{6}$/.test(value?.toString() ?? ""),
      onKeyDown: onKeyDownNumbersOnly,
    },
  },
  monthNumber: {
    field: MonthField,
    onChange: ({ value, onChange, onClose, validation }) => {
      onChange?.(value, validation);
      onClose?.();
    },
  },
  yearNumber: {
    field: YearField,
    onChange: ({ value, onChange, onClose, validation }) => {
      onChange?.(value, validation);
      onClose?.();
    },
  },
  radio: {
    field: RadioButtonsProxy,
  },
  checkboxes: {
    field: CheckboxGroupProxy,
  },
  hidden: {
    field: HiddenFieldProxy,
  },
};
